import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    LuCheck, LuChevronDown, LuChevronLeft, LuChevronRight, LuClock, LuCode, LuCopy, LuExternalLink, LuEye,
    LuGlobe, LuKeyRound, LuListFilter, LuPlug, LuRefreshCw, LuSearch, LuTriangleAlert,
} from 'react-icons/lu';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import PageHeader from '../components/PageHeader';
import MonthPicker from '../components/MonthPicker';
import InfoTitulo from '../components/InfoTitulo';
import RecordPanel, { PanelSection } from '../components/RecordPanel';
import { ListCard, ListCards, ListRow, PerPage, RowAction, RowActions } from '../components/ListKit';
import { useChartTokens, useTooltipStyle } from '../components/ChartKit';
import { useCelular } from '../hooks/useCelular';
import { nomeEtapa } from '../data/etapas';
import aviso from '../lib/aviso';
import {
    SiteSistemaService,
    type SiteCadastro,
    type SiteChave,
    type SiteConexao,
    type SiteFormularioTeste,
    type SiteGoogle,
    type SiteLead,
    type SiteNoAr,
    type SiteResumo,
} from '../services/SiteSistemaService';
import './SiteSistemaPage.css';

/**
 * Site — área Sistema (só GB Code), Relatórios › Site. Etapa A da fase 7.
 *
 * Os leads que o brikcrm.com.br manda para a entrada "Site" da empresa dona: quem
 * deixou contato, por qual ferramenta, com qual resultado e o que virou. É o
 * RELATÓRIO do canal e a ponte para a oportunidade — o atendimento mora nela.
 *
 * Direção B da proposta (relatório no molde da Visão geral), aprovada com T1–T6:
 * docs/designsystem/propostas/admin/fase-7-site-tela-a.html. O seletor de site (T3)
 * entra só na etapa B; o endereço continua visível depois de conectar (T4); a linha
 * mostra etapa e responsável (T6).
 *
 * Estados da conexão (§3 da proposta): não conectado (E1, só o aviso com o botão),
 * conectando (E2, painel com o endereço), esperando o primeiro lead (E3) e sem lead
 * há 7 dias ou mais (E4, bloco em alerta e uma linha no topo). Desde 17/10, E3 e E4 deram
 * lugar ao autoteste: a API confere se o site tem o endereço da entrada e a tela só avisa
 * quando acha defeito (sem variável, endereço de outra entrada ou site fora do ar).
 *
 * Etapa C (17/10/2026): a aba Leads conta os três caminhos do site, e não só o
 * formulário. (1) Formulário: o lead da entrada. (2) Botão do WhatsApp: a conversa
 * que começou com o texto que o botão deixa escrito; quem também preencheu o
 * formulário conta uma vez só. (3) Teste grátis: a empresa cujo cadastro veio de um
 * link do site (a origem passou a ser gravada em 17/10; antes disso não há como saber).
 *
 * Etapa B: a segunda aba do cabeçalho, "Google" (§6 da proposta da tela A). O período
 * é o mesmo das duas abas; o seletor de site (D3, brikcrm.com.br por padrão) aparece só
 * nela, porque só ali os dados mudam de um site para o outro. Os blocos são os da §3 de
 * fase-7-site.html: o resumo do período, de onde vêm, o que buscam, quais páginas
 * trabalham e a saúde do site. No topo, o "no ar" (verificado a cada abertura) e a data
 * dos dados do Google (coleta diária, D2) com o "Atualizar agora".
 *
 * Tela única (17/10/2026): as abas Leads e Google viraram uma página só, porque a
 * pergunta de quem abre é uma só — o site trouxe cliente? No topo, o caminho do site
 * à conta (visitas → cliques em Testar grátis → contas criadas → usando → assinaram);
 * logo abaixo, quem criou conta, com por onde chegou ao site e a página de entrada;
 * depois as visitas, e quem chamou pelo formulário ou pelo WhatsApp. O que é do CRM
 * (contas e contatos) só existe para o brikcrm.com.br: na useBrik a tela é só o Google.
 */

type Modo = 'mes' | '30d';

const CHAVE_MODO = 'sis-site-modo';
const CHAVE_MES = 'sis-site-mes';
const CHAVE_SITE = 'sis-site-site';

const SITES: { chave: SiteChave; nome: string }[] = [
    { chave: 'brikcrm', nome: 'brikcrm.com.br' },
    { chave: 'usebrik', nome: 'usebrik.com.br' },
];

const nomeDoSite = (chave: SiteChave) => SITES.find(s => s.chave === chave)?.nome ?? 'brikcrm.com.br';

const COLUNAS = '110px minmax(0, 1fr) 190px 120px 160px 150px 78px';
const POR_PAGINA = [25, 50, 100] as const;

/** O que dizer quando o autoteste acha defeito. "ok" e "sem_teste" (site antigo, sem a rota) não dizem nada. */
const defeitoDoFormulario = (f: SiteFormularioTeste | null): { titulo: string; texto: string } | null => {
    if (!f) return null;
    if (f.situacao === 'sem_variavel') return {
        titulo: 'Os contatos do formulário do site não estão chegando.',
        texto: `O site está no ar, mas sem o endereço de entrega (${VARIAVEL_DO_SITE}). Copie o endereço em "Ver conexão" e coloque no servidor do site.`,
    };
    if (f.situacao === 'endereco_errado') return {
        titulo: 'O formulário do site entrega os contatos em outro lugar.',
        texto: `O endereço que está no servidor do site (${VARIAVEL_DO_SITE}) não é o desta entrada. Copie o endereço em "Ver conexão" e troque lá.`,
    };
    if (f.situacao === 'site_fora') return {
        titulo: 'Não foi possível testar o formulário do site.',
        texto: 'O site não respondeu ao teste. Confira se ele está no ar.',
    };
    return null;
};

/** Etapas que são desfecho: pintadas na linha, como na proposta. */
const ETAPA_GANHO = 3;
const ETAPA_PERDIDO = 6;

const VARIAVEL_DO_SITE = 'BRIK_WEBHOOK_ENTRADA_URL';

/* ── datas e números ───────────────────────────────────────────── */

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const mesCorrente = () => iso(new Date()).slice(0, 7);

const limitesDoMes = (mes: string) => {
    const [ano, m] = mes.split('-').map(Number);
    const ultimo = new Date(ano, m, 0).getDate();
    return { de: `${mes}-01`, ate: `${mes}-${String(ultimo).padStart(2, '0')}` };
};

const ultimos30Dias = () => {
    const hoje = new Date();
    const inicio = new Date(hoje);
    inicio.setDate(hoje.getDate() - 29);
    return { de: iso(inicio), ate: iso(hoje) };
};

/** Todos os dias do período, para o gráfico não pular os dias sem lead. */
const diasDoPeriodo = (de: string, ate: string) => {
    const dias: string[] = [];
    const d = new Date(`${de.slice(0, 10)}T00:00:00`);
    const fim = new Date(`${ate.slice(0, 10)}T00:00:00`);
    while (d <= fim && dias.length < 400) {
        dias.push(iso(d));
        d.setDate(d.getDate() + 1);
    }
    return dias;
};

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

const numero = (v: number) => v.toLocaleString('pt-BR');
const plural = (v: number, um: string, varios: string) => `${numero(v)} ${v === 1 ? um : varios}`;

const doisDig = (n: number) => String(n).padStart(2, '0');
const diaMes = (d: Date) => `${doisDig(d.getDate())}/${doisDig(d.getMonth() + 1)}`;
const hora = (d: Date) => `${doisDig(d.getHours())}:${doisDig(d.getMinutes())}`;

/** "10/11 14:32" — a coluna Recebido. */
const dataHoraCurta = (valor: string) => { const d = new Date(valor); return `${diaMes(d)} ${hora(d)}`; };
/** "10/11, 14:32" — no painel. */
const dataHora = (valor: string) => { const d = new Date(valor); return `${diaMes(d)}, ${hora(d)}`; };

/** "hoje, 14:32" · "ontem, 09:10" · "02/11, 18:40". */
const quando = (valor: string) => {
    const d = new Date(valor);
    const hoje = new Date();
    const ontem = new Date(hoje);
    ontem.setDate(hoje.getDate() - 1);
    if (iso(d) === iso(hoje)) return `hoje, ${hora(d)}`;
    if (iso(d) === iso(ontem)) return `ontem, ${hora(d)}`;
    return dataHora(valor);
};

/** `5511984721150` → `(11) 98472-1150`. O que não tiver cara de telefone volta como veio. */
const telefoneLegivel = (valor: string | null) => {
    if (!valor) return null;
    let d = valor.replace(/\D/g, '');
    if ((d.length === 12 || d.length === 13) && d.startsWith('55')) d = d.slice(2);
    if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return valor;
};

const iniciais = (nome: string | null) => {
    const partes = (nome ?? '').trim().split(/\s+/).filter(Boolean);
    if (partes.length === 0) return '?';
    const ultima = partes.length > 1 ? partes[partes.length - 1][0] : '';
    return (partes[0][0] + ultima).toUpperCase();
};

const semAcento = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/* ── o que o site mandou ───────────────────────────────────────── */

/** `dados` e `utm` podem chegar como objeto ou como JSON em texto. */
const comoObjeto = (valor: unknown): Record<string, unknown> | null => {
    if (!valor) return null;
    if (typeof valor === 'string') {
        try {
            const o = JSON.parse(valor);
            return o && typeof o === 'object' && !Array.isArray(o) ? o as Record<string, unknown> : null;
        } catch { return null; }
    }
    return typeof valor === 'object' && !Array.isArray(valor) ? valor as Record<string, unknown> : null;
};

/** "calculadoraDeCusto" / "dias_uteis" → "Calculadora de custo" / "Dias uteis". */
const rotuloDaChave = (chave: string) => {
    const t = chave.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase().trim();
    return t.charAt(0).toUpperCase() + t.slice(1);
};

const valorLegivel = (v: unknown): string => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'number') return numero(v);
    if (typeof v === 'boolean') return v ? 'sim' : 'não';
    if (Array.isArray(v)) return v.every(x => typeof x !== 'object') ? v.map(valorLegivel).join(', ') : JSON.stringify(v);
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v);
};

/** O nome longo da ferramenta, de `origem` ("site: ferramenta: calculadora de custo da API"). */
const descricaoFerramenta = (l: SiteLead) => {
    if (l.canal === 'whatsapp') return l.ferramenta ?? 'Botão do WhatsApp';
    const origem = (l.origem ?? '').replace(/^site:\s*/i, '').replace(/^ferramenta:\s*/i, '').trim();
    if (/^contato$/i.test(origem)) return 'Formulário de contato';
    if (!origem) return l.ferramenta ?? 'Site';
    return origem.charAt(0).toUpperCase() + origem.slice(1);
};

/** O diagnóstico é o único com desenho próprio: a nota por tema É o resultado (§4). */
const diagnosticoDe = (l: SiteLead) => {
    const d = comoObjeto(l.dados);
    if (!d || typeof d.nota !== 'number' || !Array.isArray(d.porTema)) return null;
    const temas = (d.porTema as unknown[])
        .map(t => comoObjeto(t))
        .filter((t): t is Record<string, unknown> => !!t && typeof t.tema === 'string' && typeof t.nota === 'number')
        .map(t => ({ tema: t.tema as string, nota: t.nota as number }));
    if (temas.length === 0) return null;
    // A faixa ("Vendendo no improviso") só vem escrita no resumo: "tirei 46/100 (Vendendo no improviso)".
    const faixa = l.resumo?.match(/\/100\s*\(([^)]+)\)/)?.[1] ?? null;
    return { nota: d.nota as number, faixa, temas };
};

const utmLegivel = (l: SiteLead) => {
    const u = comoObjeto(l.utm);
    if (!u) return null;
    const pega = (k: string) => {
        const v = u[`utm_${k}`] ?? u[k];
        return typeof v === 'string' && v.trim() ? v.trim() : null;
    };
    const partes = [pega('source'), pega('medium'), pega('campaign')].filter(Boolean);
    return partes.length ? partes.join(' · ') : null;
};

/** "Em teste até 30/10" · "Assinante" · "Teste vencido". */
const situacaoDoCadastro = (c: SiteCadastro) => {
    if (c.situacao === 'assinante') return 'Assinante';
    if (c.situacao === 'vencido') return 'Teste vencido';
    return c.assinaturaExpira ? `Em teste até ${diaMes(new Date(c.assinaturaExpira))}` : 'Em teste';
};

/* ── a página ──────────────────────────────────────────────────── */

const SiteSistemaPage = () => {
    const navigate = useNavigate();
    const celular = useCelular();

    const [modo, setModo] = useState<Modo>(() => (sessionStorage.getItem(CHAVE_MODO) as Modo) || '30d');
    const [mes, setMes] = useState(() => sessionStorage.getItem(CHAVE_MES) || mesCorrente());
    const [site, setSite] = useState<SiteChave>(() => (sessionStorage.getItem(CHAVE_SITE) === 'usebrik' ? 'usebrik' : 'brikcrm'));

    const [conexao, setConexao] = useState<SiteConexao | null>(null);
    const [erroConexao, setErroConexao] = useState<string | null>(null);
    const [conectando, setConectando] = useState(false);
    /** O painel da conexão: 'novo' logo depois de conectar (com "Concluir"), 'ver' pelo botão. */
    const [painelConexao, setPainelConexao] = useState<'novo' | 'ver' | null>(null);

    const [resumo, setResumo] = useState<SiteResumo | null>(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState<string | null>(null);

    const [busca, setBusca] = useState('');
    const [ferramenta, setFerramenta] = useState<string | null>(null);
    const [pagina, setPagina] = useState(1);
    const [porPagina, setPorPagina] = useState<number>(POR_PAGINA[0]);
    const [aberto, setAberto] = useState<SiteLead | null>(null);

    useEffect(() => { sessionStorage.setItem(CHAVE_MODO, modo); }, [modo]);
    useEffect(() => { sessionStorage.setItem(CHAVE_MES, mes); }, [mes]);
    useEffect(() => { sessionStorage.setItem(CHAVE_SITE, site); }, [site]);

    /* O período é um só para as duas abas. "Últimos 30 dias" é recalculado só quando
       o modo ou o mês mudam — senão cada render pediria os dados de novo. */
    const periodo = useMemo(() => (modo === 'mes' ? limitesDoMes(mes) : ultimos30Dias()), [modo, mes]);

    const carregarConexao = useCallback(async (silencioso = false) => {
        try {
            const c = await SiteSistemaService.conexao();
            setConexao(c);
            setErroConexao(null);
        } catch (e) {
            if (!silencioso) setErroConexao(e instanceof Error ? e.message : 'Não foi possível carregar a conexão.');
        }
    }, []);

    useEffect(() => { carregarConexao(); }, [carregarConexao]);

    /* As contas e os contatos são do CRM e só existem para o brikcrm.com.br. Não
       dependem da conexão do formulário: o WhatsApp e o teste grátis chegam sem ela. */
    const doCrm = site === 'brikcrm';
    const estado: 'carregando' | 'erro' | 'desconectado' | 'conectado' =
        erroConexao ? 'erro'
            : !conexao ? 'carregando'
                : !conexao.conectado ? 'desconectado'
                    : 'conectado';

    /* O autoteste do formulário (17/10): a API pergunta ao site se ele tem o endereço desta
       entrada. Com tudo certo, a tela não diz nada; só o defeito vira aviso. */
    const defeito = estado === 'conectado' ? defeitoDoFormulario(conexao?.formulario ?? null) : null;

    useEffect(() => {
        if (!doCrm) return;
        let cancelado = false;
        const { de, ate } = periodo;

        setCarregando(true);
        setErro(null);

        SiteSistemaService.resumo(de, ate)
            .then(r => { if (!cancelado) setResumo(r); })
            .catch(e => { if (!cancelado) setErro(e instanceof Error ? e.message : 'Não foi possível carregar.'); })
            .finally(() => { if (!cancelado) setCarregando(false); });

        return () => { cancelado = true; };
    }, [doCrm, periodo]);

    useEffect(() => { setPagina(1); }, [busca, ferramenta, porPagina, modo, mes]);

    const conectar = async () => {
        setConectando(true);
        try {
            const c = await SiteSistemaService.conectar();
            setConexao(c);
            setPainelConexao('novo');
        } catch (e) {
            aviso.erro(e instanceof Error ? e.message : 'Não foi possível conectar o site.');
        } finally {
            setConectando(false);
        }
    };

    const filtrados = useMemo(() => {
        const lista = resumo?.lista ?? [];
        const termo = semAcento(busca.trim());
        const digitos = busca.replace(/\D/g, '');
        return lista.filter(l => {
            if (ferramenta && (l.ferramenta ?? '') !== ferramenta) return false;
            if (!termo) return true;
            if (semAcento(l.nome ?? '').includes(termo)) return true;
            if (semAcento(l.email ?? '').includes(termo)) return true;
            return digitos.length >= 3 && (l.telefone ?? '').replace(/\D/g, '').includes(digitos);
        });
    }, [resumo, busca, ferramenta]);

    const totalPaginas = Math.max(1, Math.ceil(filtrados.length / porPagina));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const inicio = (paginaAtual - 1) * porPagina;
    const visiveis = filtrados.slice(inicio, inicio + porPagina);

    const periodoCurto = modo === 'mes'
        ? `${MESES[Number(mes.slice(5, 7)) - 1]} de ${mes.slice(0, 4)}`
        : 'últimos 30 dias';

    /* Mês ou últimos 30 dias: um controle só para a tela inteira. */
    const controlePeriodo = (
        <>
            <div className="sst-seg" role="group" aria-label="Período">
                <button type="button" className={modo === 'mes' ? 'is-on' : ''} onClick={() => setModo('mes')}>Mês</button>
                <button type="button" className={modo === '30d' ? 'is-on' : ''} onClick={() => setModo('30d')}>Últimos 30 dias</button>
            </div>
            {modo === 'mes' && <MonthPicker value={mes} onChange={setMes} />}
        </>
    );

    const r = carregando ? null : resumo;

    /* Quem chamou pelo site: o formulário (a entrada do site) e o botão do WhatsApp.
       O estado da conexão do formulário vira um aviso em cima da lista, e não a tela
       inteira: o WhatsApp e as contas chegam com ou sem ele. */
    const secaoContatos = doCrm ? (
        <>
            <div className="sst-lt-h">
                <div>
                    <div className="sst-lt-t">Quem chamou pelo site</div>
                    <div className="sst-lt-s">
                        {r
                            ? <>{plural(r.leads, 'pessoa', 'pessoas')}: {numero(r.leadsFormulario)} pelo formulário, {numero(r.leadsWhatsapp)} pelo WhatsApp · {numero(r.emAberto)} em aberto · {plural(r.ganhos, 'virou venda', 'viraram venda')}</>
                            : 'pelo formulário e pelo botão do WhatsApp'}
                        {conexao?.conectado && (
                            <> · <button type="button" className="sst-lk" onClick={() => setPainelConexao('ver')}>Ver conexão</button></>
                        )}
                    </div>
                </div>
                <div className="sst-flt">
                    <label className="lk-search sst-busca">
                        <LuSearch size={13} />
                        <input
                            type="search"
                            value={busca}
                            onChange={e => setBusca(e.target.value)}
                            placeholder="Buscar por nome, telefone ou e-mail…"
                            aria-label="Buscar contato"
                        />
                    </label>
                    <FiltroFerramenta
                        valor={ferramenta}
                        opcoes={resumo?.porFerramenta ?? []}
                        onChange={setFerramenta}
                    />
                </div>
            </div>

            {estado === 'erro' && <div className="sst-aviso is-erro">{erroConexao}</div>}
            {erro && <div className="sst-aviso is-erro">{erro}</div>}

            {estado === 'desconectado' && (
                <div className="sst-av" role="status">
                    <LuPlug size={16} />
                    <span>
                        <b>O formulário do site ainda não manda os contatos para cá.</b>{' '}
                        Conectar cria a entrada "Site brikcrm.com.br" e mostra o endereço para colar no servidor do site.
                    </span>
                    <button type="button" className="sst-btn is-primary" onClick={conectar} disabled={conectando}>
                        <LuPlug size={13} />
                        <span>{conectando ? 'Conectando…' : 'Conectar o site'}</span>
                    </button>
                </div>
            )}

            {defeito && (
                <div className="sst-av" role="status">
                    <LuTriangleAlert size={16} />
                    <span>
                        <b>{defeito.titulo}</b>{' '}{defeito.texto}
                    </span>
                    <button type="button" className="sst-btn" onClick={() => setPainelConexao('ver')}>
                        <LuPlug size={13} />
                        <span>Ver conexão</span>
                    </button>
                </div>
            )}

            <div className="lk-table sst-tabela sst-contatos">
                {celular ? (
                    <ListCards>
                        {carregando
                            ? Array.from({ length: 4 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                            : visiveis.map(l => (
                                <ListCard
                                    key={l.chave}
                                    titulo={l.nome || 'Sem nome'}
                                    apoio={`${l.ferramenta ?? '—'} · ${dataHoraCurta(l.recebidoEm)}`}
                                    estado={`${l.etapaId ? nomeEtapa(l.etapaId) : '—'}${l.responsavel ? ` · ${l.responsavel}` : ''}`}
                                    onOpen={() => setAberto(l)}
                                    label={`Ver ${l.nome ?? 'contato'}`}
                                    selected={aberto?.chave === l.chave}
                                />
                            ))}
                    </ListCards>
                ) : (
                    <div className="lk-scroll">
                        <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                            <div className="lk-head">Recebido</div>
                            <div className="lk-head">Nome</div>
                            <div className="lk-head">Ferramenta</div>
                            <div className="lk-head">Veio de</div>
                            <div className="lk-head">Etapa</div>
                            <div className="lk-head">Responsável</div>
                            <div className="lk-head lk-head-acts">Ações</div>
                        </div>
                        <div className="lk-body">
                            {carregando
                                ? Array.from({ length: 4 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                : visiveis.map(l => (
                                    <ListRow key={l.chave} columns={COLUNAS} selected={aberto?.chave === l.chave}>
                                        <span className="lk-cell lk-muted sst-num">{dataHoraCurta(l.recebidoEm)}</span>
                                        <span className="lk-cell"><span className="sst-nome">{l.nome || 'Sem nome'}</span></span>
                                        <span className="lk-cell"><span>{l.ferramenta ?? '—'}</span></span>
                                        <span className="lk-cell lk-muted">
                                            {l.canal === 'whatsapp'
                                                ? <span className="lk-empty-value" title="O WhatsApp não diz de que página a pessoa clicou">—</span>
                                                : <span>{l.veioDe || 'Direto'}</span>}
                                        </span>
                                        <span className="lk-cell"><Etapa id={l.etapaId} /></span>
                                        <span className="lk-cell lk-muted">
                                            {l.responsavel ? <span>{l.responsavel}</span> : <span className="lk-empty-value">—</span>}
                                        </span>
                                        <RowActions>
                                            <RowAction icon={<LuEye size={15} />} label={`Ver ${l.nome ?? 'contato'}`} onClick={() => setAberto(l)} />
                                        </RowActions>
                                    </ListRow>
                                ))}
                        </div>
                    </div>
                )}

                {!carregando && filtrados.length === 0 && (
                    <div className="lk-blank">
                        {(resumo?.lista.length ?? 0) === 0 ? (
                            <>
                                <div className="lk-blank-title">Ninguém chamou pelo site no período</div>
                                <div className="lk-blank-text">
                                    {conexao?.ultimoLeadEm
                                        ? `O último contato pelo formulário chegou ${quando(conexao.ultimoLeadEm)}. Troque o período no cabeçalho para vê-lo.`
                                        : 'Nem pelo formulário, nem pelo botão do WhatsApp.'}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="lk-blank-title">Ninguém com esse filtro</div>
                                <div className="lk-blank-text">Apague a busca ou volte a ferramenta para "todas".</div>
                            </>
                        )}
                    </div>
                )}

                {!carregando && filtrados.length > 0 && (
                    <div className="lk-foot">
                        <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />
                        <span className="lk-range">
                            {inicio + 1}–{Math.min(inicio + porPagina, filtrados.length)} de {filtrados.length}
                        </span>
                        <span className="lk-pages">
                            <button type="button" className="lk-page" aria-label="Página anterior"
                                disabled={paginaAtual === 1} onClick={() => setPagina(p => Math.max(1, p - 1))}>
                                <LuChevronLeft size={13} />
                            </button>
                            <span className="lk-page is-current">{paginaAtual}</span>
                            <span className="lk-muted">de {totalPaginas}</span>
                            <button type="button" className="lk-page" aria-label="Próxima página"
                                disabled={paginaAtual === totalPaginas} onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}>
                                <LuChevronRight size={13} />
                            </button>
                        </span>
                    </div>
                )}
            </div>
        </>
    ) : null;

    return (
        <div className="page-shell lk-screen sst">
            <PageHeader
                title="Site"
                titleInfo={<InfoSite />}
                subtitle={doCrm
                    ? `O que o ${nomeDoSite(site)} traz: visitas, quem criou conta, quem está usando e quem chamou`
                    : `As visitas do ${nomeDoSite(site)}: de onde vêm, o que buscam no Google e quais páginas trabalham`}
                actions={
                    <div className="sst-acoes">
                        <SeletorSite valor={site} onChange={setSite} />
                        {controlePeriodo}
                    </div>
                }
            />

            <Relatorio
                site={site}
                de={periodo.de}
                ate={periodo.ate}
                modo={modo}
                periodoCurto={periodoCurto}
                resumo={doCrm ? r : undefined}
                contatos={secaoContatos}
            />

            <PainelLead
                lead={aberto}
                nomeEntrada={conexao?.nome ?? null}
                onClose={() => setAberto(null)}
                onAbrirOportunidade={id => navigate(`/pipeline/deal/${id}`)}
            />

            <PainelConexao
                modo={painelConexao}
                conexao={conexao}
                onClose={() => setPainelConexao(null)}
            />
        </div>
    );
};

/* ── blocos de número ──────────────────────────────────────────── */

const Bloco = ({ rotulo, valor, apoio, alerta, denominador, bola }: {
    rotulo: string; valor: string; apoio?: ReactNode; alerta?: boolean; denominador?: string; bola?: boolean;
}) => (
    <div className={`sst-tile ${alerta ? 'is-alerta' : ''}`}>
        <div className="sst-tile-k">{rotulo}</div>
        <div className="sst-tile-v">
            {bola && <span className="sst-bola" aria-hidden="true" />}
            {valor}
            {denominador && <small>{denominador}</small>}
        </div>
        {apoio && <div className="sst-tile-d">{apoio}</div>}
    </div>
);

/* ── lista ─────────────────────────────────────────────────────── */

const Etapa = ({ id }: { id: number | null }) => {
    if (!id) return <span className="lk-empty-value">—</span>;
    const nome = nomeEtapa(id);
    if (id === ETAPA_GANHO) return <span className="sst-ganho">{nome}</span>;
    if (id === ETAPA_PERDIDO) return <span className="sst-perdido">{nome}</span>;
    return <span>{nome}</span>;
};

/** "Ferramenta: todas ⌄" — a pílula da proposta, com as contagens no menu. */
const FiltroFerramenta = ({ valor, opcoes, onChange }: {
    valor: string | null;
    opcoes: { ferramenta: string; leads: number }[];
    onChange: (v: string | null) => void;
}) => {
    const [aberto, setAberto] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!aberto) return;
        const fora = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false);
        };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
    }, [aberto]);

    const escolher = (v: string | null) => { onChange(v); setAberto(false); };
    const total = opcoes.reduce((s, o) => s + o.leads, 0);

    return (
        <div className="lk-filter sst-filtro" ref={ref}>
            <button
                type="button"
                className={`lk-pill ${valor ? 'is-on' : ''}`}
                aria-expanded={aberto}
                aria-haspopup="menu"
                onClick={() => setAberto(v => !v)}
            >
                <LuListFilter size={13} className="sst-pill-ic" />
                <span>Ferramenta: <b>{valor ?? 'todas'}</b></span>
                <LuChevronDown size={13} className="sst-pill-ic" />
            </button>

            {aberto && (
                <div className="lk-pop" role="menu">
                    <button type="button" className={!valor ? 'is-on' : ''} onClick={() => escolher(null)}>
                        <span>Todas</span>
                        <span className="lk-pop-count">{numero(total)}</span>
                    </button>
                    {opcoes.map(o => (
                        <button key={o.ferramenta} type="button" className={valor === o.ferramenta ? 'is-on' : ''}
                            onClick={() => escolher(o.ferramenta)}>
                            <span>{o.ferramenta}</span>
                            <span className="lk-pop-count">{numero(o.leads)}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

/* ── quem criou conta ──────────────────────────────────────────── */

const COLUNAS_CONTA = '96px minmax(0, 1fr) 92px minmax(0, 1fr) 160px 128px';

/** A última entrada foi no mesmo dia do cadastro: a pessoa criou a conta e não voltou. */
const soNoCadastro = (c: SiteCadastro) => !!c.ultimaSessao && iso(new Date(c.ultimaSessao)) === iso(new Date(c.criadoEm));

/** "Usando · hoje, 08:49" · "Só no dia do cadastro" · "Parou · 02/11, 18:40" · "Nunca entrou". */
const UsoDaConta = ({ c }: { c: SiteCadastro }) => {
    if (!c.ultimaSessao) return <span className="lk-muted">Nunca entrou</span>;
    if (c.usando) return <span><span className="sst-ganho">Usando</span><span className="lk-muted"> · {quando(c.ultimaSessao)}</span></span>;
    if (soNoCadastro(c)) return <span className="lk-muted">Só no dia do cadastro</span>;
    return <span className="lk-muted">Parou · {quando(c.ultimaSessao)}</span>;
};

/** A primeira página que abriu ("/" é a inicial); sem ela (cadastro antigo), o botão de onde saiu. */
const entradaDaConta = (c: SiteCadastro) => {
    if (c.entrada) return c.entrada === '/' ? 'Página inicial' : c.entrada;
    return c.pagina ? `botão ${c.pagina}` : null;
};

/**
 * As contas de teste grátis que saíram de um botão do site, com o caminho até ali: por onde a
 * pessoa chegou ao site e a primeira página que abriu (o site anota e manda no cadastro desde
 * 17/10). Sem painel: tudo cabe na linha, e quem cuida da conta é Sistema › Empresas.
 */
const ContasCriadas = ({ resumo }: { resumo: SiteResumo | null }) => {
    const celular = useCelular();
    const lista = resumo?.listaCadastros ?? [];

    return (
        <>
            <div className="sst-lt-h">
                <div>
                    <div className="sst-lt-t">Quem criou conta pelo site</div>
                    <div className="sst-lt-s">
                        {resumo
                            ? `${plural(resumo.cadastros, 'conta', 'contas')} de ${numero(resumo.cadastrosTotal)} criadas no período · ${plural(resumo.cadastrosUsando, 'usando', 'usando')} · ${plural(resumo.cadastrosAssinantes, 'assinou', 'assinaram')}`
                            : 'contas de teste grátis que saíram de um botão do site'}
                    </div>
                </div>
            </div>

            <div className="lk-table sst-tabela sst-contas">
                {!resumo ? (
                    <div className="lk-skeleton" />
                ) : lista.length === 0 ? (
                    <div className="lk-blank">
                        <div className="lk-blank-title">Ninguém criou conta pelo site no período</div>
                        <div className="lk-blank-text">
                            {resumo.cadastrosTotal > 0
                                ? `${plural(resumo.cadastrosTotal, 'conta foi criada', 'contas foram criadas')} no período, sem vir de um botão do site.`
                                : 'Nenhuma conta foi criada no período.'}
                        </div>
                    </div>
                ) : celular ? (
                    <ListCards>
                        {lista.map(c => (
                            <ListCard
                                key={c.empresaId}
                                titulo={c.empresa}
                                apoio={[c.fonte, entradaDaConta(c), `criada ${dataHoraCurta(c.criadoEm)}`].filter(Boolean).join(' · ')}
                                estado={c.usando ? 'Usando' : situacaoDoCadastro(c)}
                                label={c.empresa}
                            />
                        ))}
                    </ListCards>
                ) : (
                    <div className="lk-scroll">
                        <div className="lk-row" style={{ gridTemplateColumns: COLUNAS_CONTA }}>
                            <div className="lk-head">Criada</div>
                            <div className="lk-head">Conta</div>
                            <div className="lk-head">Veio de</div>
                            <div className="lk-head">Entrou no site por</div>
                            <div className="lk-head">Uso do CRM</div>
                            <div className="lk-head">Situação</div>
                        </div>
                        <div className="lk-body">
                            {lista.map(c => {
                                const entrada = entradaDaConta(c);
                                return (
                                    <ListRow key={c.empresaId} columns={COLUNAS_CONTA}>
                                        <span className="lk-cell lk-muted sst-num">{dataHoraCurta(c.criadoEm)}</span>
                                        <span className="lk-cell">
                                            <span className="sst-nome" title={[c.admin, c.email].filter(Boolean).join(' · ') || undefined}>{c.empresa}</span>
                                        </span>
                                        <span className="lk-cell">
                                            {c.fonte ? <span>{c.fonte}</span> : <span className="lk-empty-value" title="Conta de antes de o site anotar por onde a pessoa chegou">—</span>}
                                        </span>
                                        <span className="lk-cell lk-muted">
                                            {entrada
                                                ? <span title={c.pagina ? `Clicou no botão ${c.pagina}` : undefined}>{entrada}</span>
                                                : <span className="lk-empty-value">—</span>}
                                        </span>
                                        <span className="lk-cell"><UsoDaConta c={c} /></span>
                                        <span className="lk-cell">
                                            <span className={c.situacao === 'assinante' ? 'sst-ganho' : c.situacao === 'vencido' ? 'lk-muted' : ''}>
                                                {situacaoDoCadastro(c)}
                                            </span>
                                        </span>
                                    </ListRow>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

/* ── painel do lead (§4 da proposta) ───────────────────────────── */

/**
 * O resultado primeiro, porque é o que só esta tela tem; depois contato, de onde
 * veio e a oportunidade. O único botão é "Abrir oportunidade". Sem lixeira: o lead
 * é o registro do que o site mandou.
 */
const PainelLead = ({ lead, nomeEntrada, onClose, onAbrirOportunidade }: {
    lead: SiteLead | null;
    nomeEntrada: string | null;
    onClose: () => void;
    onAbrirOportunidade: (conversaId: number) => void;
}) => {
    if (!lead) return null;
    return (
        <RecordPanel
            open
            onClose={onClose}
            initials={iniciais(lead.nome)}
            title={lead.nome || 'Sem nome'}
            subtitle={`${descricaoFerramenta(lead)} · ${dataHora(lead.recebidoEm)}`}
            actions={lead.conversaId ? (
                <button type="button" className="rp-btn is-primary" onClick={() => onAbrirOportunidade(lead.conversaId as number)}>
                    <LuExternalLink size={13} />
                    <span>Abrir oportunidade</span>
                </button>
            ) : undefined}
        >
            <ConteudoLead key={lead.chave} lead={lead} nomeEntrada={nomeEntrada} />
        </RecordPanel>
    );
};

const ConteudoLead = ({ lead, nomeEntrada }: { lead: SiteLead; nomeEntrada: string | null }) => {
    const [cru, setCru] = useState(false);

    const diagnostico = diagnosticoDe(lead);
    const dados = comoObjeto(lead.dados);
    const utm = comoObjeto(lead.utm);
    const linhas = (lead.resumo ?? '').split(/\r?\n/).map(t => t.trim()).filter(Boolean);
    const pares = dados ? Object.entries(dados) : [];
    const temCru = !!dados || !!utm;
    const telefone = telefoneLegivel(lead.telefone);
    const campanha = utmLegivel(lead);

    const verCru = temCru && (
        <>
            <button type="button" className="sst-raw" onClick={() => setCru(v => !v)} aria-expanded={cru}>
                <LuCode size={13} />
                <span>{cru ? 'Esconder o que o site enviou' : 'Ver o que o site enviou'}</span>
            </button>
            {cru && <pre className="sst-pre">{JSON.stringify({ dados: dados ?? undefined, utm: utm ?? undefined }, null, 2)}</pre>}
        </>
    );

    return (
        <>
            {diagnostico ? (
                <PanelSection title="O que respondeu">
                    <div className="sst-nota">
                        <b>{numero(diagnostico.nota)}</b>
                        <span>de 100{diagnostico.faixa ? ` · ${diagnostico.faixa}` : ''}</span>
                    </div>
                    {diagnostico.temas.map(t => (
                        <div className="sst-mini" key={t.tema}>
                            <span>{t.tema}</span>
                            <span className="sst-rk-b"><i style={{ width: `${Math.max(0, Math.min(100, t.nota))}%` }} /></span>
                            <span className="sst-rk-v">{numero(t.nota)}</span>
                        </div>
                    ))}
                    {verCru}
                </PanelSection>
            ) : linhas.length > 0 ? (
                <PanelSection title="Resultado da ferramenta">
                    <div className="sst-res">
                        {linhas.map((t, i) => <p key={i} className={/^total\b/i.test(t) ? 'is-total' : ''}>{t}</p>)}
                    </div>
                    {verCru}
                </PanelSection>
            ) : pares.length > 0 ? (
                /* Lead de antes da T1 (sem `resumo`): os campos de `dados` como rótulo e valor. */
                <PanelSection title="Resultado da ferramenta">
                    <div className="rp-kv">
                        {pares.map(([k, v]) => (
                            <Fragment key={k}>
                                <span className="rp-k">{rotuloDaChave(k)}</span>
                                <span className="rp-v">{valorLegivel(v)}</span>
                            </Fragment>
                        ))}
                    </div>
                    {verCru}
                </PanelSection>
            ) : !lead.mensagem ? (
                <PanelSection title="Resultado da ferramenta">
                    <p className="sst-nada">O site não mandou resultado nem mensagem.</p>
                    {verCru}
                </PanelSection>
            ) : null}

            {lead.mensagem && (
                <PanelSection title="O que escreveu">
                    <div className="sst-res"><p>{lead.mensagem}</p></div>
                    {!diagnostico && linhas.length === 0 && pares.length === 0 && verCru}
                </PanelSection>
            )}

            <PanelSection title="Contato">
                <div className="rp-kv">
                    <span className="rp-k">WhatsApp</span>
                    <span className={`rp-v ${telefone ? '' : 'sst-falta'}`}>{telefone ?? 'não informado'}</span>
                    <span className="rp-k">E-mail</span>
                    <span className={`rp-v ${lead.email ? '' : 'sst-falta'}`}>{lead.email || 'não informado'}</span>
                </div>
            </PanelSection>

            {lead.canal === 'whatsapp' ? (
                <PanelSection title="De onde veio">
                    <div className="rp-kv">
                        <span className="rp-k">Canal</span>
                        <span className="rp-v">{lead.ferramenta ?? 'Botão do WhatsApp'} do site</span>
                        <span className="rp-k">Página</span>
                        <span className="rp-v sst-falta">o WhatsApp não informa</span>
                    </div>
                </PanelSection>
            ) : (
            <PanelSection title="De onde veio">
                <div className="rp-kv">
                    <span className="rp-k">Página</span>
                    <span className={`rp-v ${lead.pagina ? '' : 'sst-falta'}`}>{lead.pagina || 'não informada'}</span>
                    <span className="rp-k">Veio de</span>
                    <span className="rp-v">{lead.veioDe || 'Direto (sem site de origem)'}</span>
                    {campanha && (
                        <>
                            <span className="rp-k">Campanha</span>
                            <span className="rp-v">{campanha}</span>
                        </>
                    )}
                </div>
            </PanelSection>
            )}

            <PanelSection title="Oportunidade">
                {lead.conversaId ? (
                    <div className="rp-kv">
                        <span className="rp-k">Etapa</span>
                        <span className="rp-v">{lead.etapaId ? nomeEtapa(lead.etapaId) : '—'}</span>
                        <span className="rp-k">Responsável</span>
                        <span className={`rp-v ${lead.responsavel ? '' : 'sst-falta'}`}>{lead.responsavel || 'sem responsável'}</span>
                        <span className="rp-k">{lead.canal === 'whatsapp' ? 'Primeira mensagem' : 'Aberta em'}</span>
                        <span className="rp-v">
                            {dataHora(lead.recebidoEm)}
                            {lead.canal === 'whatsapp' ? ', pelo WhatsApp' : nomeEntrada ? `, pela entrada "${nomeEntrada}"` : ''}
                        </span>
                    </div>
                ) : (
                    <p className="sst-nada">Este lead não abriu oportunidade.</p>
                )}
            </PanelSection>
        </>
    );
};

/* ── painel da conexão (E2) ────────────────────────────────────── */

/**
 * Os quatro passos: a entrada (feito pelo Brik), o endereço para copiar, e os dois
 * que ficam fora do Brik. Não termina em "conectado": termina em "faça um teste".
 * Pelo botão "Ver conexão" o mesmo painel volta, com o endereço à vista (T4).
 */
const PainelConexao = ({ modo, conexao, onClose }: {
    modo: 'novo' | 'ver' | null;
    conexao: SiteConexao | null;
    onClose: () => void;
}) => {
    const [copiado, setCopiado] = useState(false);

    useEffect(() => {
        if (!copiado) return;
        const id = window.setTimeout(() => setCopiado(false), 2000);
        return () => window.clearTimeout(id);
    }, [copiado]);

    if (!modo || !conexao) return null;

    const chegou = !!conexao.ultimoLeadEm;

    const copiar = async () => {
        if (!conexao.url) return;
        try {
            await navigator.clipboard.writeText(conexao.url);
            setCopiado(true);
        } catch {
            aviso.erro('Não foi possível copiar. Selecione o endereço e copie à mão.');
        }
    };

    return (
        <RecordPanel
            open
            onClose={onClose}
            title={modo === 'novo' ? 'Conectar o site' : 'Conexão do site'}
            subtitle="brikcrm.com.br"
            persistentFooter={modo === 'novo'}
            footer={modo === 'novo' ? (
                <button type="button" className="rp-btn is-primary sst-fim" onClick={onClose}>Concluir</button>
            ) : undefined}
        >
            <section className="rp-sec">
                <div className="sst-pp">
                    <span className="sst-pn is-ok"><LuCheck size={13} /></span>
                    <span className="sst-pp-t">Entrada criada</span>
                    <span className="sst-pp-d">
                        "{conexao.nome ?? 'Site brikcrm.com.br'}", abrindo oportunidade e distribuindo entre a equipe.
                        Ela aparece também em Apps, onde se troca o responsável.
                        {conexao.criadaEm ? ` Criada em ${dataHora(conexao.criadaEm)}.` : ''}
                    </span>
                </div>
            </section>

            <section className="rp-sec">
                <div className="sst-pp">
                    <span className="sst-pn">2</span>
                    <span className="sst-pp-t">Copie o endereço</span>
                    <div className="sst-pp-d is-forte">
                        <div className="sst-url">{conexao.url ?? 'O endereço não veio da API.'}</div>
                        <button type="button" className={`rp-btn sst-copiar ${copiado ? 'is-ok' : ''}`} onClick={copiar} disabled={!conexao.url}>
                            {copiado ? <LuCheck size={13} /> : <LuCopy size={13} />}
                            <span>{copiado ? 'Copiado' : 'Copiar'}</span>
                        </button>
                        <p className="sst-pp-nota">O endereço é a senha da entrada: quem o tiver consegue mandar lead.</p>
                    </div>
                </div>
            </section>

            <section className="rp-sec">
                <div className="sst-pp">
                    <span className={`sst-pn ${chegou ? 'is-ok' : ''}`}>{chegou ? <LuCheck size={13} /> : '3'}</span>
                    <span className="sst-pp-t">Cole no servidor do site</span>
                    <span className="sst-pp-d">
                        Na variável <code className="sst-code">{VARIAVEL_DO_SITE}</code> do brikcrm.com.br, e publique o site
                        de novo. Sem ela, o site guarda o contato só no log do servidor.
                    </span>
                </div>
            </section>

            <section className="rp-sec">
                <div className="sst-pp">
                    <span className={`sst-pn ${chegou ? 'is-ok' : ''}`}>{chegou ? <LuCheck size={13} /> : '4'}</span>
                    <span className="sst-pp-t">Faça um teste</span>
                    <span className="sst-pp-d">
                        {chegou && conexao.ultimoLeadEm
                            ? `Os leads já chegam: o endereço está no servidor. O último foi ${quando(conexao.ultimoLeadEm)}.`
                            : 'Preencha o formulário em brikcrm.com.br/contato. O lead aparece nesta tela em segundos, e o ntfy avisa.'}
                    </span>
                </div>
            </section>
        </RecordPanel>
    );
};

/* ══ etapa B: a aba Google ════════════════════════════════════════ */

/**
 * Os eventos de conversão do site, na língua de quem lê. Os nomes técnicos são os
 * que o site manda ao GA4 (C:/Git/brik/src/lib/analytics.ts). O vídeo junta dois
 * eventos numa célula só, porque "quantos deram play" só diz algo ao lado de
 * "quantos foram até o fim".
 */
const EVENTOS: { rotulo: string; chaves: string[] }[] = [
    { rotulo: 'Leads deixados', chaves: ['generate_lead'] },
    { rotulo: 'Cliques em Testar grátis', chaves: ['cta_trial_click'] },
    { rotulo: 'Cliques no WhatsApp', chaves: ['cta_whatsapp_click'] },
    { rotulo: 'Ferramentas usadas', chaves: ['ferramenta_usada'] },
    { rotulo: 'Vídeo: play / assistiu até o fim', chaves: ['video_play', 'video_completo'] },
    { rotulo: 'Modelos copiados', chaves: ['modelo_copiado'] },
];

/** As seções do site novo (as pastas de src/app/(site) no repositório do site). */
const SECOES: Record<string, string> = {
    solucoes: 'Soluções',
    segmentos: 'Segmentos',
    ferramentas: 'Ferramentas',
    conteudo: 'Conteúdo',
    modelos: 'Modelos',
    glossario: 'Glossário',
    comparativos: 'Comparativos',
    alternativas: 'Alternativas',
    melhores: 'Melhores',
    precos: 'Preços',
    contato: 'Contato',
    sobre: 'Sobre',
    privacy: 'Privacidade',
};

/** Tabelas dos cartões: o bastante para ler o topo sem um cartão ficar muito mais alto que o vizinho. */
const LINHAS_TABELA = 8;
/** Origens com barra própria; o resto vira "Outras". */
const ORIGENS_VISIVEIS = 7;

/** 0..1 → "54%". */
const porcento = (v: number) => `${Math.round(v * 100)}%`;
const decimal = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const fatia = (parte: number, todo: number) => (todo > 0 ? Math.round((parte / todo) * 100) : 0);
/** "2026-10-07" → "07/10", sem passar por Date (o fuso não pode puxar para o dia anterior). */
const diaMesIso = (s: string) => `${s.slice(8, 10)}/${s.slice(5, 7)}`;

/** "nos últimos 30 dias" · "em outubro de 2026". */
const noPeriodo = (periodoCurto: string) => (periodoCurto === 'últimos 30 dias' ? 'nos últimos 30 dias' : `em ${periodoCurto}`);

/** "hoje às 06:10" · "ontem às 22:40" · "em 07/10 às 06:10". */
const atualizadoQuando = (valor: string) => {
    const d = new Date(valor);
    const hoje = new Date();
    const ontem = new Date(hoje);
    ontem.setDate(hoje.getDate() - 1);
    if (iso(d) === iso(hoje)) return `hoje às ${hora(d)}`;
    if (iso(d) === iso(ontem)) return `ontem às ${hora(d)}`;
    return `em ${diaMes(d)} às ${hora(d)}`;
};

const nomeDaSecao = (secao: string) => {
    const chave = semAcento(secao.trim().replace(/^\/+|\/+$/g, ''));
    if (!chave || chave === 'home' || chave === 'inicio' || chave === 'raiz') return 'Página inicial';
    return SECOES[chave] ?? chave.charAt(0).toUpperCase() + chave.slice(1);
};

/** O caminho do sitemap sem o domínio: "https://brikcrm.com.br/sitemap.xml" → "/sitemap.xml". */
const caminhoCurto = (caminho: string) => caminho.replace(/^https?:\/\/[^/]+/i, '') || caminho;

/**
 * Até que dia vão as visitas. O GA4 fecha o dia na madrugada seguinte: a série vai
 * até o último dia que a API mandou, ou até a véspera da coleta — o que vier depois.
 * Desenhar o resto do período como zero inventaria uma queda que não houve.
 */
const visitasAte = (dados: SiteGoogle): string | null => {
    const dias = dados.porDia.map(p => p.dia.slice(0, 10)).sort();
    const ultimo = dias.length ? dias[dias.length - 1] : null;
    let vespera: string | null = null;
    if (dados.atualizadoEm) {
        const d = new Date(dados.atualizadoEm);
        d.setDate(d.getDate() - 1);
        vespera = iso(d);
    }
    if (ultimo && vespera) return ultimo > vespera ? ultimo : vespera;
    return ultimo ?? vespera;
};

/** O "i" do título: de onde vem cada número e quando. */
const InfoSite = () => (
    <InfoTitulo rotulo="De onde vêm os números">
        <span className="rl-info-t">De onde vêm os números</span>
        <span className="rl-info-d">
            <b>Visitas, origem, páginas e eventos</b>
            Google Analytics (GA4), só do endereço do site escolhido: a propriedade é a mesma para o brikcrm.com.br e a useBrik.
        </span>
        <span className="rl-info-d">
            <b>Contas criadas</b>
            Do próprio CRM: as contas de teste grátis cujo cadastro saiu de um botão do brikcrm.com.br. Desde 17/10 o site
            anota por onde a pessoa chegou (Google, Instagram…) e a primeira página que abriu, e manda junto no cadastro.
        </span>
        <span className="rl-info-d">
            <b>Usando</b>
            Alguém da conta voltou ao CRM depois do dia do cadastro e entrou nos últimos 7 dias. O modo suporte não conta.
        </span>
        <span className="rl-info-d">
            <b>Quem chamou</b>
            O formulário do site (a entrada "Site brikcrm.com.br") e o botão do WhatsApp, reconhecido pelo texto que ele deixa escrito.
        </span>
        <span className="rl-info-d">
            <b>Buscas e sitemaps</b>
            Search Console, que entrega os dados com 2 a 3 dias de atraso.
        </span>
        <span className="rl-info-d">
            <b>Quando</b>
            A API coleta uma vez por dia, de madrugada. "Atualizar agora" força uma coleta na hora.
        </span>
        <span className="rl-info-d">
            <b>Site no ar</b>
            Conferido a cada abertura desta tela, direto no endereço do site, sem esperar a coleta.
        </span>
        <span className="rl-info-d">
            <b>Engajamento</b>
            A régua do GA4: a visita ficou 10 segundos ou mais, viu duas páginas ou fez uma conversão.
        </span>
    </InfoTitulo>
);

/** "brikcrm.com.br ⌄" — o seletor de site (D3). Acende quando sai do padrão. */
const SeletorSite = ({ valor, onChange }: { valor: SiteChave; onChange: (v: SiteChave) => void }) => {
    const [aberto, setAberto] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!aberto) return;
        const fora = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false);
        };
        document.addEventListener('mousedown', fora);
        return () => document.removeEventListener('mousedown', fora);
    }, [aberto]);

    return (
        <div className="lk-filter sst-filtro" ref={ref}>
            <button
                type="button"
                className={`lk-pill sst-site ${valor !== 'brikcrm' ? 'is-on' : ''}`}
                aria-expanded={aberto}
                aria-haspopup="menu"
                aria-label="Site"
                onClick={() => setAberto(v => !v)}
            >
                <LuGlobe size={13} className="sst-pill-ic" />
                <span>{nomeDoSite(valor)}</span>
                <LuChevronDown size={13} className="sst-pill-ic" />
            </button>

            {aberto && (
                <div className="lk-pop" role="menu">
                    {SITES.map(s => (
                        <button key={s.chave} type="button" className={valor === s.chave ? 'is-on' : ''}
                            onClick={() => { onChange(s.chave); setAberto(false); }}>
                            <span>{s.nome}</span>
                            {valor === s.chave && <LuCheck size={13} />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * A tela inteira abaixo do cabeçalho. Os dados do Google vêm do cache diário da API; o
 * `resumo` (contas e contatos) vem do CRM e só existe para o brikcrm.com.br — `undefined`
 * na useBrik, `null` enquanto carrega.
 */
const Relatorio = ({ site, de, ate, modo, periodoCurto, resumo, contatos }: {
    site: SiteChave; de: string; ate: string; modo: Modo; periodoCurto: string;
    resumo: SiteResumo | null | undefined; contatos: ReactNode;
}) => {
    const [dados, setDados] = useState<SiteGoogle | null>(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState<string | null>(null);
    const [atualizando, setAtualizando] = useState(false);
    const [tentativa, setTentativa] = useState(0);

    /* O "Atualizar agora" pode voltar depois de a pessoa trocar de site ou de período:
       a resposta só entra se ainda for do recorte que está na tela. */
    const recorte = `${site}|${de}|${ate}`;
    const recorteAtual = useRef(recorte);
    recorteAtual.current = recorte;

    useEffect(() => {
        let cancelado = false;
        setCarregando(true);
        setErro(null);
        setDados(null);

        SiteSistemaService.google(de, ate, site)
            .then(d => { if (!cancelado) setDados(d); })
            .catch(e => { if (!cancelado) setErro(e instanceof Error ? e.message : 'Não foi possível carregar os dados do Google.'); })
            .finally(() => { if (!cancelado) setCarregando(false); });

        return () => { cancelado = true; };
    }, [site, de, ate, tentativa]);

    const atualizar = async () => {
        const pedido = recorte;
        setAtualizando(true);
        const id = aviso.carregando('Buscando os dados no Google…');
        try {
            const d = await SiteSistemaService.atualizarGoogle(de, ate, site);
            if (recorteAtual.current === pedido) setDados(d);
            aviso.ok('Dados do Google atualizados.', { id });
        } catch (e) {
            aviso.erro(e instanceof Error ? e.message : 'Não foi possível atualizar agora.', { id });
        } finally {
            setAtualizando(false);
        }
    };

    const doCrm = resumo !== undefined;

    if (carregando) {
        return <div className="page-body"><EsqueletoGoogle /></div>;
    }

    /* Sem o Google, o que é do CRM continua de pé: quem criou conta e quem chamou
       não dependem do Analytics. */
    if (erro || !dados) {
        return (
            <div className={`page-body ${doCrm ? '' : 'sst-cheio'}`}>
                <div className={`sst-vz ${doCrm ? 'is-solto' : ''}`}>
                    <div className="sst-vz-in">
                        <span className="sst-vz-ic is-erro"><LuTriangleAlert size={16} /></span>
                        <div className="sst-vz-t">Não foi possível carregar os dados do Google</div>
                        <p className="sst-vz-d">{erro ?? 'A API não devolveu os dados.'}</p>
                        <button type="button" className="sst-btn" onClick={() => setTentativa(t => t + 1)}>
                            <LuRefreshCw size={13} />
                            <span>Tentar de novo</span>
                        </button>
                    </div>
                </div>
                {doCrm && <ContasCriadas resumo={resumo} />}
                {contatos}
            </div>
        );
    }

    const semVisitas = dados.configurado && dados.visitas === 0 && dados.porDia.every(p => p.visitas === 0);
    const temSearchConsole = dados.buscas !== null || dados.sitemaps !== null;
    const temBuscasOuSaude = (dados.buscas?.length ?? 0) > 0 || (dados.sitemaps?.length ?? 0) > 0;
    /* Sem nada embaixo, o aviso ocupa a área inteira e fica no meio dela (sem vão no pé). */
    const avisoEnche = !doCrm && (!dados.configurado || (semVisitas && !temBuscasOuSaude));

    return (
        <div className={`page-body sst-g ${avisoEnche ? 'sst-cheio' : ''}`}>
            <FaixaGoogle dados={dados} atualizando={atualizando} onAtualizar={atualizar} />

            {doCrm && <Funil dados={dados} resumo={resumo} modo={modo} />}
            {doCrm && <ContasCriadas resumo={resumo} />}

            {!dados.configurado ? (
                <div className={`sst-vz ${doCrm ? 'is-solto' : ''}`}>
                    <div className="sst-vz-in">
                        <span className="sst-vz-ic"><LuKeyRound size={16} /></span>
                        <div className="sst-vz-t">A conta de leitura do Google não está configurada na API</div>
                        <p className="sst-vz-d">
                            Sem a chave da conta de leitura (seção <code className="sst-code">Google:SiteLeitor</code> do
                            appsettings), a API não consegue ler o Google Analytics nem o Search Console. As contas e os
                            contatos do site não dependem dela.
                        </p>
                    </div>
                </div>
            ) : (
                <>
                    <BlocosGoogle dados={dados} modo={modo} semVisitas={doCrm} />

                    {semVisitas ? (
                        <>
                            <SemVisitas site={site} periodoCurto={periodoCurto} enche={!doCrm && !temBuscasOuSaude} />
                            {contatos}
                            {temBuscasOuSaude && (
                                <div className="sst-graficos is-meio">
                                    <CardBuscas dados={dados} />
                                    <CardSaude dados={dados} />
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="sst-graficos">
                                <CardVisitasPorDia dados={dados} de={de} ate={ate} periodoCurto={periodoCurto} />
                                <CardOrigem dados={dados} periodoCurto={periodoCurto} />
                            </div>

                            <CardEventos dados={dados} site={site} periodoCurto={periodoCurto} />

                            {contatos}

                            {temSearchConsole ? (
                                <>
                                    <div className="sst-graficos is-meio">
                                        <CardPaginas dados={dados} periodoCurto={periodoCurto} />
                                        <CardBuscas dados={dados} />
                                    </div>
                                    <div className="sst-graficos is-meio">
                                        <CardSecoes dados={dados} periodoCurto={periodoCurto} />
                                        <CardSaude dados={dados} />
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="sst-graficos is-meio">
                                        <CardPaginas dados={dados} periodoCurto={periodoCurto} />
                                        <CardSecoes dados={dados} periodoCurto={periodoCurto} />
                                    </div>
                                    <SemSearchConsole site={site} />
                                </>
                            )}
                        </>
                    )}
                </>
            )}

            {!dados.configurado && contatos}
        </div>
    );
};

/* ── do site à conta ───────────────────────────────────────────── */

/** "25% dos cliques" — só quando a conta faz sentido (de 1 a 100%). */
const taxa = (parte: number, todo: number, de: string) => {
    if (todo <= 0 || parte > todo) return null;
    return `${fatia(parte, todo)}% ${de}`;
};

/**
 * O caminho do site à conta, numa faixa só: as duas primeiras etapas vêm do Google
 * Analytics, as três últimas do CRM. Cada etapa diz quanto passou da anterior, porque é
 * a queda entre elas que mostra onde o site perde gente.
 */
const Funil = ({ dados: d, resumo, modo }: { dados: SiteGoogle; resumo: SiteResumo | null; modo: Modo }) => {
    const cliques = d.eventos.find(e => e.evento === 'cta_trial_click')?.total ?? 0;
    const anterior = modo === 'mes' ? 'no mês anterior' : 'nos 30 dias anteriores';
    const delta = d.visitasAnterior > 0 ? Math.round(((d.visitas - d.visitasAnterior) / d.visitasAnterior) * 100) : null;
    const contas = resumo?.cadastros ?? null;
    const usando = resumo?.cadastrosUsando ?? null;
    const assinaram = resumo?.cadastrosAssinantes ?? null;

    const passos: { rotulo: string; valor: number | null; apoio: ReactNode; destaque?: boolean }[] = [
        {
            rotulo: 'Visitas',
            valor: d.visitas,
            apoio: (
                <>
                    {delta !== null && delta !== 0 && (
                        <span className={`sst-delta ${delta > 0 ? 'is-sobe' : 'is-desce'}`}>{delta > 0 ? '+' : '−'}{Math.abs(delta)}% · </span>
                    )}
                    {numero(d.visitasAnterior)} {anterior}
                </>
            ),
        },
        {
            rotulo: 'Clicaram em Testar grátis',
            valor: cliques,
            apoio: taxa(cliques, d.visitas, 'das visitas') ?? 'cliques no botão do site',
        },
        {
            rotulo: 'Criaram conta',
            valor: contas,
            apoio: contas === null ? '' : taxa(contas, cliques, 'dos cliques') ?? `de ${plural(resumo?.cadastrosTotal ?? 0, 'conta criada', 'contas criadas')}`,
            destaque: true,
        },
        {
            rotulo: 'Estão usando',
            valor: usando,
            apoio: usando === null || contas === null ? '' : taxa(usando, contas, 'das contas') ?? 'voltaram ao CRM na semana',
        },
        {
            rotulo: 'Assinaram',
            valor: assinaram,
            apoio: assinaram === null || contas === null ? '' : taxa(assinaram, contas, 'das contas') ?? 'já pagam',
        },
    ];

    return (
        <div className="sst-card sst-fn-card">
            <div className="sst-card-t">Do site à conta</div>
            <div className="sst-card-s">visitas e cliques: Google Analytics · contas, uso e assinatura: o próprio CRM</div>
            <div className="sst-fn">
                {passos.map((p, i) => (
                    <div className={`sst-fn-p ${p.destaque ? 'is-destaque' : ''}`} key={p.rotulo}>
                        {i > 0 && <LuChevronRight size={14} className="sst-fn-seta" aria-hidden="true" />}
                        <span className="sst-fn-k">{p.rotulo}</span>
                        <span className="sst-fn-v">{p.valor === null ? '—' : numero(p.valor)}</span>
                        <span className="sst-fn-d">{p.apoio}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

/* ── topo: no ar + data dos dados ──────────────────────────────── */

/**
 * Dois relógios lado a lado, de propósito: o "no ar" é da hora (a API confere a cada
 * abertura), os dados do Google são da última coleta. Fora do ar, a faixa inteira
 * vira alerta — é a primeira pergunta de quem abre a aba.
 */
const FaixaGoogle = ({ dados, atualizando, onAtualizar }: {
    dados: SiteGoogle; atualizando: boolean; onAtualizar: () => void;
}) => {
    const noAr = dados.noAr ?? null;
    const va = visitasAte(dados);
    const ba = dados.buscasAte ? dados.buscasAte.slice(0, 10) : null;

    let ateQuando: string;
    if (va && ba && va !== ba) ateQuando = `Visitas até ${diaMesIso(va)} · buscas até ${diaMesIso(ba)}`;
    else if (va || ba) ateQuando = `Dados até ${diaMesIso((va ?? ba) as string)}`;
    else ateQuando = 'Nenhum dado coletado ainda';

    return (
        <div className={`sst-gb ${noAr && !noAr.ok ? 'is-fora' : ''}`} role="status">
            <FraseNoAr noAr={noAr} />
            {dados.configurado && (
                <div className="sst-gb-r">
                    <span className="sst-gb-d">
                        {ateQuando}
                        {dados.atualizadoEm ? ` · atualizado ${atualizadoQuando(dados.atualizadoEm)}` : ''}
                    </span>
                    <button type="button" className="sst-btn" onClick={onAtualizar} disabled={atualizando}>
                        <LuRefreshCw size={13} className={atualizando ? 'sst-gira' : ''} />
                        <span>{atualizando ? 'Atualizando…' : 'Atualizar agora'}</span>
                    </button>
                </div>
            )}
        </div>
    );
};

const FraseNoAr = ({ noAr }: { noAr: SiteNoAr | null }) => {
    if (!noAr) {
        return (
            <span className="sst-gb-ar is-sem">
                <span className="sst-bola" aria-hidden="true" />
                <b>Sem verificação</b>
                <span>a API não disse se o site está no ar</span>
            </span>
        );
    }
    const verificado = `verificado às ${hora(new Date(noAr.verificadoEm))}`;
    if (noAr.ok) {
        return (
            <span className="sst-gb-ar" title={noAr.url}>
                <span className="sst-bola" aria-hidden="true" />
                <b>Site no ar</b>
                <span>{noAr.tempoMs !== null ? `respondeu em ${numero(noAr.tempoMs)} ms · ` : ''}{verificado}</span>
            </span>
        );
    }
    return (
        <span className="sst-gb-ar" title={noAr.url}>
            <LuTriangleAlert size={15} aria-hidden="true" />
            <b>Site fora do ar</b>
            <span>{noAr.status ? `erro ${noAr.status}` : 'não respondeu'} · {verificado}</span>
        </span>
    );
};

/* ── os números ────────────────────────────────────────────────── */

/** Com o funil em cima (brikcrm.com.br), as visitas moram nele e saem daqui: `semVisitas`. */
const BlocosGoogle = ({ dados: d, modo, semVisitas = false }: { dados: SiteGoogle; modo: Modo; semVisitas?: boolean }) => {
    const anterior = modo === 'mes' ? 'no mês anterior' : 'nos 30 dias anteriores';
    const delta = d.visitasAnterior > 0 ? Math.round(((d.visitas - d.visitasAnterior) / d.visitasAnterior) * 100) : null;
    const t = d.buscasTotais;
    return (
        <div className={`sst-tiles ${semVisitas ? 'is-5' : 'is-6'}`}>
            {!semVisitas && <Bloco
                rotulo="Visitas"
                valor={numero(d.visitas)}
                apoio={
                    <>
                        {delta !== null && delta !== 0 && (
                            <span className={`sst-delta ${delta > 0 ? 'is-sobe' : 'is-desce'}`}>{delta > 0 ? '+' : '−'}{Math.abs(delta)}% · </span>
                        )}
                        {numero(d.visitasAnterior)} {anterior}
                    </>
                }
            />}
            <Bloco
                rotulo="Pessoas"
                valor={numero(d.usuarios)}
                apoio={d.usuarios > 0 ? `${decimal(d.visitas / d.usuarios)} visitas por pessoa` : 'pessoas diferentes'}
            />
            <Bloco
                rotulo="Engajamento"
                valor={d.visitas > 0 ? porcento(d.engajamento) : '—'}
                apoio="das visitas com interação"
            />
            <Bloco
                rotulo="Do Google"
                valor={numero(d.visitasGoogle)}
                apoio={d.visitas > 0 ? `${fatia(d.visitasGoogle, d.visitas)}% das visitas` : 'visitas vindas da busca'}
            />
            <Bloco
                rotulo="De IAs"
                valor={numero(d.visitasIas)}
                apoio={d.visitas > 0 ? `${fatia(d.visitasIas, d.visitas)}% das visitas` : 'ChatGPT e outras'}
            />
            <Bloco
                rotulo="Cliques nas buscas"
                valor={t ? numero(t.cliques) : '—'}
                apoio={!t
                    ? 'Search Console não liberado'
                    : t.impressoes > 0
                        ? `em ${plural(t.impressoes, 'impressão', 'impressões')}`
                        : 'sem impressões ainda'}
            />
        </div>
    );
};

/* ── os cartões ────────────────────────────────────────────────── */

const Cartao = ({ titulo, apoio, children, className }: {
    titulo: string; apoio: string; children: ReactNode; className?: string;
}) => (
    <div className={`sst-card ${className ?? ''}`}>
        <div className="sst-card-t">{titulo}</div>
        <div className="sst-card-s">{apoio}</div>
        {children}
    </div>
);

const CardVisitasPorDia = ({ dados, de, ate, periodoCurto }: {
    dados: SiteGoogle; de: string; ate: string; periodoCurto: string;
}) => {
    const tokens = useChartTokens();
    const tooltip = useTooltipStyle();

    const { serie, fim } = useMemo(() => {
        const limite = visitasAte(dados);
        const fimSerie = limite && limite < ate ? limite : ate;
        const contagem = new Map(dados.porDia.map(p => [p.dia.slice(0, 10), p.visitas]));
        return {
            fim: fimSerie,
            serie: diasDoPeriodo(de, fimSerie)
                .map(dia => ({ dia, rotulo: diaMesIso(dia), visitas: contagem.get(dia) ?? 0 })),
        };
    }, [dados, de, ate]);

    const eixo = { axisLine: false, tickLine: false, tick: { fill: tokens.axis, fontSize: 11 } };

    return (
        <Cartao titulo="Visitas por dia" apoio={`${periodoCurto}${fim < ate ? ` · até ${diaMesIso(fim)}` : ''}`}>
            <ResponsiveContainer width="100%" height={185}>
                {/* Margem à direita: o rótulo do último dia não pode ser cortado na borda. */}
                <BarChart data={serie} margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke={tokens.grid} vertical={false} />
                    <XAxis dataKey="rotulo" {...eixo} interval={serie.length > 16 ? 6 : 1} />
                    <YAxis {...eixo} width={34} allowDecimals={false} />
                    <Tooltip
                        {...tooltip}
                        cursor={{ fill: tokens.grid }}
                        formatter={(v: number) => [numero(v), 'Visitas']}
                        labelFormatter={(r: string) => `Dia ${r}`}
                    />
                    <Bar dataKey="visitas" fill={tokens.primary} radius={[3, 3, 0, 0]} maxBarSize={22} />
                </BarChart>
            </ResponsiveContainer>
        </Cartao>
    );
};

/** Barra na linha, valor absoluto e a fatia do total ao lado (padrão de ranking). */
const Ranking = ({ itens, total, vazio }: {
    itens: { rotulo: string; valor: number }[]; total: number; vazio: string;
}) => {
    if (itens.length === 0) return <div className="sst-rk-vazio">{vazio}</div>;
    const maior = Math.max(1, ...itens.map(i => i.valor));
    return (
        <div className="sst-rk is-pct">
            {itens.map(i => (
                <div className="sst-rk-l" key={i.rotulo}>
                    <span className="sst-rk-r" title={i.rotulo}>{i.rotulo}</span>
                    <span className="sst-rk-b"><i style={{ width: `${(i.valor / maior) * 100}%` }} /></span>
                    <span className="sst-rk-v">{numero(i.valor)}</span>
                    <span className="sst-rk-p">{fatia(i.valor, total)}%</span>
                </div>
            ))}
        </div>
    );
};

const CardOrigem = ({ dados, periodoCurto }: { dados: SiteGoogle; periodoCurto: string }) => {
    const itens = useMemo(() => {
        /* A API manda todas as origens, inclusive as zeradas: barra de zero não diz nada. */
        const ordem = dados.porOrigem.filter(o => o.visitas > 0).sort((a, b) => b.visitas - a.visitas);
        const topo = ordem.slice(0, ORIGENS_VISIVEIS).map(o => ({ rotulo: o.origem || 'Sem origem', valor: o.visitas }));
        const resto = ordem.slice(ORIGENS_VISIVEIS).reduce((s, o) => s + o.visitas, 0);
        return resto > 0 ? [...topo, { rotulo: 'Outras', valor: resto }] : topo;
    }, [dados]);
    const total = dados.porOrigem.reduce((s, o) => s + o.visitas, 0);

    return (
        <Cartao titulo="De onde vêm" apoio={`visitas por origem ${noPeriodo(periodoCurto)}`}>
            <Ranking itens={itens} total={total} vazio="Nenhuma origem registrada no período." />
        </Cartao>
    );
};

const CardEventos = ({ dados, site, periodoCurto }: { dados: SiteGoogle; site: SiteChave; periodoCurto: string }) => {
    const total = (chave: string) => dados.eventos.find(e => e.evento === chave)?.total ?? 0;
    /* Visitas sem nenhum evento: o site que está no ar ainda não manda esses eventos
       (quem manda é o site novo). Sem esta linha, a fileira de zeros parece defeito. */
    const nenhum = EVENTOS.every(e => e.chaves.every(c => total(c) === 0));
    return (
        <Cartao titulo="O que as visitas fizeram" apoio={`eventos de conversão do site ${noPeriodo(periodoCurto)}`} className="sst-evs-card">
            <div className="sst-evs">
                {EVENTOS.map(e => (
                    <div className="sst-ev" key={e.rotulo}>
                        <span className="sst-ev-v">
                            {e.chaves.length === 2 ? (
                                <>{numero(total(e.chaves[0]))}<small> / {numero(total(e.chaves[1]))}</small></>
                            ) : numero(total(e.chaves[0]))}
                        </span>
                        <span className="sst-ev-r">{e.rotulo}</span>
                    </div>
                ))}
            </div>
            {nenhum && (
                <div className="sst-card-n">
                    {site === 'brikcrm'
                        ? 'Nenhum evento no período. Esses eventos são enviados pelo site novo: a contagem começa quando ele estiver publicado.'
                        : 'Nenhum destes eventos no período.'}
                </div>
            )}
        </Cartao>
    );
};

const CardPaginas = ({ dados, periodoCurto }: { dados: SiteGoogle; periodoCurto: string }) => {
    const paginas = useMemo(() => dados.paginas.filter(p => p.visitas > 0).sort((a, b) => b.visitas - a.visitas), [dados]);
    const visiveis = paginas.slice(0, LINHAS_TABELA);
    return (
        <Cartao
            titulo="Páginas mais visitadas"
            apoio={`${paginas.length > LINHAS_TABELA ? `as ${LINHAS_TABELA} primeiras de ${numero(paginas.length)} · ` : ''}${noPeriodo(periodoCurto)}`}
        >
            {visiveis.length === 0 ? (
                <div className="sst-rk-vazio">Nenhuma página visitada no período.</div>
            ) : (
                <div className="sst-tb is-paginas">
                    <div className="sst-tb-h"><span>Página</span><span>Visitas</span><span>Engajamento</span></div>
                    {visiveis.map(p => (
                        <div className="sst-tb-l" key={p.pagina}>
                            <span className="sst-tb-x" title={p.pagina}>{p.pagina}</span>
                            <span>{numero(p.visitas)}</span>
                            <span>{porcento(p.engajamento)}</span>
                        </div>
                    ))}
                </div>
            )}
        </Cartao>
    );
};

/* Sem o período no subtítulo: ele já está no cabeçalho, e a linha precisa da posição média. */
const CardBuscas = ({ dados }: { dados: SiteGoogle }) => {
    const buscas = useMemo(
        () => [...(dados.buscas ?? [])].sort((a, b) => b.cliques - a.cliques || b.impressoes - a.impressoes),
        [dados],
    );
    const visiveis = buscas.slice(0, LINHAS_TABELA);
    return (
        <Cartao
            titulo="O que buscam"
            apoio={[
                'buscas no Google em que o site apareceu',
                buscas.length > LINHAS_TABELA ? `as ${LINHAS_TABELA} com mais cliques` : null,
                dados.buscasTotais && dados.buscasTotais.impressoes > 0 ? `posição média ${decimal(dados.buscasTotais.posicaoMedia)}` : null,
            ].filter(Boolean).join(' · ')}
        >
            {dados.buscas === null ? (
                <div className="sst-rk-vazio">Search Console não liberado para este site.</div>
            ) : visiveis.length === 0 ? (
                <div className="sst-rk-vazio">
                    {/* Sem `buscasAte`, o Search Console não entregou nem um dia: não é "zero buscas". */}
                    {dados.buscasAte
                        ? 'O site ainda não apareceu em nenhuma busca no período.'
                        : 'O Search Console ainda não entregou dados deste site. Eles chegam com 2 a 3 dias de atraso.'}
                </div>
            ) : (
                <div className="sst-tb is-buscas">
                    <div className="sst-tb-h"><span>Busca</span><span>Cliques</span><span>Impressões</span><span>Posição</span></div>
                    {visiveis.map(b => (
                        <div className="sst-tb-l" key={b.consulta}>
                            <span className="sst-tb-x" title={b.consulta}>{b.consulta}</span>
                            <span>{numero(b.cliques)}</span>
                            <span>{numero(b.impressoes)}</span>
                            <span>{decimal(b.posicao)}</span>
                        </div>
                    ))}
                </div>
            )}
        </Cartao>
    );
};

const CardSecoes = ({ dados, periodoCurto }: { dados: SiteGoogle; periodoCurto: string }) => {
    const itens = useMemo(
        () => dados.secoes.filter(s => s.visitas > 0).sort((a, b) => b.visitas - a.visitas)
            .slice(0, LINHAS_TABELA)
            .map(s => ({ rotulo: nomeDaSecao(s.secao), valor: s.visitas })),
        [dados],
    );
    const total = dados.secoes.reduce((s, x) => s + x.visitas, 0);
    return (
        <Cartao titulo="Páginas por seção" apoio={`visitas por parte do site ${noPeriodo(periodoCurto)}`}>
            <Ranking itens={itens} total={total} vazio="Nenhuma visita por seção no período." />
        </Cartao>
    );
};

/**
 * Saúde do site: o Google leu o sitemap, e quantas páginas dele estão indexadas.
 * O total vai em cima (é a pergunta), cada sitemap embaixo com a última leitura.
 */
const CardSaude = ({ dados }: { dados: SiteGoogle }) => {
    const sitemaps = dados.sitemaps;
    const enviadas = (sitemaps ?? []).reduce((s, m) => s + m.enviadas, 0);
    const indexadas = (sitemaps ?? []).reduce((s, m) => s + m.indexadas, 0);
    return (
        <Cartao titulo="Saúde do site" apoio="sitemaps no Search Console">
            {sitemaps === null ? (
                <div className="sst-rk-vazio">Search Console não liberado para este site.</div>
            ) : sitemaps.length === 0 ? (
                <div className="sst-rk-vazio">Nenhum sitemap enviado ao Search Console.</div>
            ) : (
                <>
                    {/* A API do Search Console marca a contagem de indexadas como obsoleta e
                       costuma mandar 0. Com tudo zerado, mostrar "0 indexadas" pareceria
                       defeito: aí vale o total enviado e o painel do Google para o resto. */}
                    {indexadas > 0 ? (
                        <>
                            <div className="sst-sd-n">
                                <b>{numero(indexadas)}</b>
                                <span>de {plural(enviadas, 'página enviada', 'páginas enviadas')} estão indexadas</span>
                            </div>
                            <span className="sst-rk-b sst-sd-b">
                                <i style={{ width: `${enviadas > 0 ? Math.min(100, (indexadas / enviadas) * 100) : 0}%` }} />
                            </span>
                        </>
                    ) : (
                        <div className="sst-sd-n">
                            <b>{numero(enviadas)}</b>
                            <span>{enviadas === 1 ? 'página enviada' : 'páginas enviadas'} ao Google · quantas estão indexadas, só no painel do Search Console</span>
                        </div>
                    )}
                    <div className="sst-sd">
                        {sitemaps.map(m => (
                            <div className="sst-sd-l" key={m.caminho}>
                                <span className="sst-sd-c" title={m.caminho}>{caminhoCurto(m.caminho)}</span>
                                <span className="sst-sd-q">
                                    {indexadas > 0 ? `${numero(m.indexadas)} de ${numero(m.enviadas)}` : plural(m.enviadas, 'enviada', 'enviadas')}
                                </span>
                                <span className="sst-sd-m">
                                    {m.lidoEm ? `lido ${quando(m.lidoEm)}` : 'ainda não lido pelo Google'}
                                </span>
                                <span className={`sst-sd-e ${m.erros > 0 ? 'is-erro' : m.avisos > 0 ? 'is-aviso' : 'is-ok'}`}>
                                    {m.erros > 0 || m.avisos > 0 ? <LuTriangleAlert size={13} /> : <LuCheck size={13} />}
                                    <span>
                                        {m.erros > 0 || m.avisos > 0
                                            ? [m.erros > 0 ? plural(m.erros, 'erro', 'erros') : null, m.avisos > 0 ? plural(m.avisos, 'aviso', 'avisos') : null].filter(Boolean).join(' · ')
                                            : 'sem erros'}
                                    </span>
                                </span>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </Cartao>
    );
};

/* ── estados da aba ────────────────────────────────────────────── */

/**
 * Zero visitas no período. No começo é o normal (o site novo ainda não foi publicado),
 * então o texto diz isso em vez de seis cartões vazios que parecem defeito.
 */
const SemVisitas = ({ site, periodoCurto, enche }: { site: SiteChave; periodoCurto: string; enche: boolean }) => (
    <div className={`sst-vz ${enche ? '' : 'is-solto'}`}>
        <div className="sst-vz-in">
            <span className="sst-vz-ic"><LuClock size={16} /></span>
            <div className="sst-vz-t">Esperando as primeiras visitas</div>
            <p className="sst-vz-d">
                {site === 'brikcrm'
                    ? <>Nenhuma visita medida {noPeriodo(periodoCurto)}. Enquanto o site novo não for publicado, é isso mesmo que se espera: as visitas começam a aparecer na coleta do dia seguinte à publicação, e as buscas, 2 a 3 dias depois.</>
                    : <>Nenhuma visita do {nomeDoSite(site)} medida {noPeriodo(periodoCurto)}. As visitas aparecem na coleta do dia seguinte à primeira que o Google Analytics registrar.</>}
            </p>
        </div>
    </div>
);

const SemSearchConsole = ({ site }: { site: SiteChave }) => (
    <div className="sst-card sst-nsc">
        <span className="sst-vz-ic"><LuSearch size={16} /></span>
        <div>
            <div className="sst-card-t">Search Console não liberado para este site</div>
            <p className="sst-nsc-d">
                O que buscam e a saúde do site dependem de a propriedade do {nomeDoSite(site)} no Search Console ser liberada
                para a conta de leitura do Brik. As visitas, que vêm do Google Analytics, já estão acima.
            </p>
        </div>
    </div>
);

const EsqueletoGoogle = () => (
    <div aria-busy="true" aria-label="Carregando os dados do Google">
        <div className="sst-sk sst-sk-faixa" />
        <div className="sst-tiles is-6">
            {Array.from({ length: 6 }).map((_, i) => (
                <div className="sst-tile" key={i}>
                    <span className="sst-sk sst-sk-k" />
                    <span className="sst-sk sst-sk-v" />
                    <span className="sst-sk sst-sk-d" />
                </div>
            ))}
        </div>
        <div className="sst-graficos">
            <div className="sst-card"><span className="sst-sk sst-sk-k" /><span className="sst-sk sst-sk-g" /></div>
            <div className="sst-card"><span className="sst-sk sst-sk-k" /><span className="sst-sk sst-sk-g" /></div>
        </div>
    </div>
);

export default SiteSistemaPage;
